import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm, SubmitHandler } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { AppDispatch } from "src/store";
import {
  verifyEmailTalent,
  verifyEmailCompany,
  verifyEmailBackoffice,
} from "src/features/authSlice";
import { AccountType } from "src/types/firebase";
import { useTranslation } from "react-i18next";

interface VerificationFormInputs {
  syncCode: string;
  verificationCode: string;
}

const EmailVerificationPage: React.FC = () => {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const prefilledSyncCode = searchParams.get("syncCode") || "";
  const email = searchParams.get("email") || "";
  const userId = searchParams.get("userId") || "";
  const accountType = (searchParams.get("accountType") || "talent") as AccountType;

  // Memoize schema to prevent recreation on every render
  const schema = useMemo(
    () => Yup.object().shape({
      syncCode: Yup.string().required(t("emailVerification.syncCode") + " " + t("common.validation.required")),
      verificationCode: Yup.string().required(t("emailVerification.verificationCode") + " " + t("common.validation.required")),
    }),
    [t]
  );
  const resolver = useMemo(() => yupResolver(schema), [schema]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<VerificationFormInputs>({
    resolver: resolver as any,
    defaultValues: {
      syncCode: prefilledSyncCode,
      verificationCode: "",
    },
  });

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { loading, error } = useSelector((state: any) => state.auth);

  useEffect(() => {
    if (prefilledSyncCode) {
      setValue("syncCode", prefilledSyncCode);
    }
  }, [prefilledSyncCode, setValue]);

  useEffect(() => {
    if (!email || !userId) {
      navigate("/login");
    }
  }, [email, userId, navigate]);

  const onSubmit: SubmitHandler<VerificationFormInputs> = async (data) => {
    // Call appropriate verifyEmail action based on accountType
    const result = await dispatch(
      accountType === "talent"
        ? verifyEmailTalent({
            ...data,
            email,
            userId,
          })
        : accountType === "company"
        ? verifyEmailCompany({
            ...data,
            email,
            userId,
          })
        : verifyEmailBackoffice({
            ...data,
            email,
            userId,
          })
    );

    // Check if verification was successful
    if (
      result.type === "auth/verifyEmailTalent/fulfilled" ||
      result.type === "auth/verifyEmailCompany/fulfilled" ||
      result.type === "auth/verifyEmailBackoffice/fulfilled"
    ) {
      setActiveStep(1);
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    }
  };

  const steps = [t("emailVerification.steps.enterCode"), t("emailVerification.steps.verified")];

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: "100%" }}>
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            {t("emailVerification.title")}
          </Typography>

          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {activeStep === 0 && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                {t("emailVerification.infoMessage")}{" "}
                <strong>{email}</strong>
              </Alert>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Box
                component="form"
                onSubmit={handleSubmit(onSubmit)}
                sx={{ mt: 1 }}
              >
                <TextField
                  {...register("syncCode")}
                  margin="normal"
                  required
                  fullWidth
                  id="syncCode"
                  label={t("emailVerification.syncCode")}
                  error={!!errors.syncCode}
                  helperText={
                    errors.syncCode?.message ||
                    (prefilledSyncCode ? t("emailVerification.prefilledHelper") : "")
                  }
                  InputProps={{
                    readOnly: !!prefilledSyncCode,
                  }}
                />

                <TextField
                  {...register("verificationCode")}
                  margin="normal"
                  required
                  fullWidth
                  id="verificationCode"
                  label={t("emailVerification.verificationCode")}
                  error={!!errors.verificationCode}
                  helperText={errors.verificationCode?.message}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={loading}
                >
                  {loading ? t("emailVerification.verifying") : t("emailVerification.verifyButton")}
                </Button>
              </Box>
            </>
          )}

          {activeStep === 1 && (
            <Alert severity="success">
              <Typography variant="h6" gutterBottom>
                {t("emailVerification.successTitle")}
              </Typography>
              <Typography variant="body2">
                {t("emailVerification.redirecting")}
              </Typography>
            </Alert>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default EmailVerificationPage;
